import { fetchSurveyStatistics, transformSurveyStatistics } from '@/services/survey'
import type { ProcessedStatistics } from '@/types/survey'
import { useQuery } from '@tanstack/vue-query'
import type { ComputedRef } from 'vue'
import { computed } from 'vue'

/**
 * Vue Query hook for fetching and transforming survey statistics
 *
 * @param year - Computed ref for the selected year
 * @param branch - Computed ref for the selected branch ID (optional)
 * @param mosque - Computed ref for the selected mosque ID (optional)
 * @returns Vue Query result with transformed statistics
 *
 * @example
 * const year = computed(() => 2024)
 * const branch = computed(() => selectedBranches.value[0])
 * const surveyStats = useSurveyStatistics(year, branch)
 */
export const useSurveyStatistics = (
  year: ComputedRef<number>,
  branch?: ComputedRef<number | undefined>,
  mosque?: ComputedRef<number | undefined>
) => {
  return useQuery({
    queryKey: ['survey-statistics', year, branch, mosque],
    queryFn: async () => {
      const rawData = await fetchSurveyStatistics(
        year.value,
        branch?.value,
        mosque?.value
      )
      return transformSurveyStatistics(rawData)
    },
    staleTime: 1000 * 60 * 5, // 5 minutes
    enabled: computed(() => year.value >= 2024)
  })
}
